import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@obis/firebase-config';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { PlatformConfig } from '../config/platformConfig';
import GoLiveCountdown from './shared/GoLiveCountdown';

const ModuleLauncher: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const id = tenantSlug || 'default-tenant';
  const navigate = useNavigate();

  const [steps, setSteps] = useState<Record<string, boolean>>({
    profile: false,
    students: false,
    configureFees: false,
    inviteTeachers: false,
    firstAttendance: false,
    firstFee: false
  });
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const docRef = doc(db, 'onboarding', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.steps) setSteps(data.steps);
          if (data.progressPercentage !== undefined) setProgress(data.progressPercentage);
        }
      } catch (err) {
        console.error("Error fetching onboarding status", err);
      }
    };
    if (id) fetchStatus();
  }, [id]);

  const checklistItems = [
    { key: 'profile', label: 'Complete school profile', description: 'Set up your school name and info.', path: `/app/${id}/settings/school-profile` },
    { key: 'students', label: 'Upload students', description: 'Import your existing students.', path: `/app/${id}/crm/students/import` },
    { key: 'configureFees', label: 'Configure fee structure', description: 'Set up admission and tuition fees.', path: `/app/${id}/finance/fees` },
    { key: 'inviteTeachers', label: 'Invite teachers', description: 'Add your teaching staff.', path: `/app/${id}/hrms/staff/invite` },
    { key: 'firstAttendance', label: 'First attendance marked', description: 'Take attendance for the first time.', path: `/app/${id}/erp/attendance` },
    { key: 'firstFee', label: 'First fee collected', description: 'Record your first fee payment.', path: `/app/${id}/finance/collection` }
  ];

  // Auto-navigate to first incomplete task
  const handleContinueSetup = () => {
    const firstIncomplete = checklistItems.find(item => !steps[item.key]);
    if (firstIncomplete?.path) {
      navigate(firstIncomplete.path);
    } else {
      // fallback
      window.location.href = PlatformConfig.getModuleUrl('erp');
    }
  };

  // Emotional Progress Text Logic
  const renderProgressMessage = () => {
    if (progress === 0) {
      return (
        <p className="text-[12px] text-blue-700 font-bold leading-relaxed">
          You're just getting started 🚀<br/><span className="text-[11px] font-medium text-blue-600">Complete 1 step to unlock your first module.</span>
        </p>
      );
    } else if (progress > 0 && progress < 50) {
       return (
        <p className="text-[12px] text-blue-700 font-bold leading-relaxed">
          Awesome start! You have momentum 🚀<br/><span className="text-[11px] font-medium text-blue-600">Knock out a few more to unlock the ERP module.</span>
        </p>
       );
    } else if (progress >= 50 && progress < 100) {
       return (
        <p className="text-[12px] text-emerald-700 font-bold leading-relaxed">
          Great progress! You're halfway to go-live 🔥<br/><span className="text-[11px] font-medium text-emerald-600">Just a few final touches to unlock the entire ecosystem.</span>
        </p>
       );
    } else {
       return (
        <p className="text-[12px] text-emerald-700 font-bold leading-relaxed">
          Incredible! You are ready to launch 🚀<br/><span className="text-[11px] font-medium text-emerald-600">All ecosystem platforms are now unlocked.</span>
        </p>
       );
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up mt-2 max-w-7xl mx-auto transform scale-[0.97] origin-top">
      {/* Title Header */}
      <div className="text-left mb-6 border-b border-gray-200 pb-4">
        <h2 className="text-3xl font-black text-gray-800 tracking-tight">🚀 Launch Your Digital Campus</h2>
        <p className="text-gray-500 text-sm mt-1 font-medium">Follow this guided launch system to activate your ecosystem modules.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
        
        {/* LEFT: Checklist */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col transition-all hover:shadow-md">
           <div className="flex items-center gap-3 mb-5 border-b border-gray-50 pb-3">
             <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm">1</div>
             <h3 className="text-lg font-bold text-gray-800">Setup Checklist</h3>
           </div>
           
           <div className="space-y-3 flex-1">
             {checklistItems.map((item) => {
               const isDone = steps[item.key];
               return (
                 <button 
                   key={item.key} 
                   onClick={() => navigate(item.path)}
                   className={`w-full text-left p-3 rounded-xl border transition-all duration-300 flex items-start justify-between gap-3 group relative overflow-hidden ${
                     isDone 
                       ? 'bg-emerald-50/50 border-emerald-100 hover:bg-emerald-50' 
                       : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md hover:-translate-y-0.5'
                   }`}
                 >
                   {/* Hover Gradient Overlay */}
                   {!isDone && <div className="absolute inset-0 bg-gradient-to-r from-blue-50/0 via-blue-50/0 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity"></div>}

                   <div className="flex items-start gap-3 relative z-10 w-full">
                     <div className={`mt-0.5 flex-shrink-0 ${isDone ? 'text-emerald-500' : 'text-gray-300 group-hover:text-blue-400 transition-colors'}`}>
                       {isDone ? <CheckCircleIcon className="w-5 h-5 animate-fade-in" /> : <div className="w-4 h-4 rounded-full border-2 border-current ml-0.5"></div>}
                     </div>
                     <div className="flex-1">
                       <h4 className={`font-bold text-xs ${isDone ? 'text-gray-500' : 'text-gray-800 group-hover:text-blue-700 transition-colors'}`}>{item.label}</h4>
                       <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">{item.description}</p>
                     </div>
                     {!isDone && (
                        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center h-full pt-1">
                           <ArrowRightIcon className="w-4 h-4 text-blue-500" />
                        </div>
                     )}
                   </div>
                 </button>
               )
             })}
           </div>
        </div>

        {/* CENTER: Setup Progress + CTA */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col transition-all hover:shadow-md relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gray-100">
               <div className="h-full bg-blue-500 transition-all duration-1000" style={{ width: `${progress}%` }}></div>
            </div>
            
            <div className="flex items-center gap-3 mb-4 border-b border-gray-50 pb-3 mt-1">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm">2</div>
              <h3 className="text-lg font-bold text-gray-800">Launch Activation</h3>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center text-center">
               <div className="relative w-44 h-44 flex items-center justify-center group my-2">
                 {/* SVG Circular Progress Bar */}
                 <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                   <circle cx="50" cy="50" r="42" className="text-gray-100 stroke-current" strokeWidth="6" fill="transparent" />
                   <circle 
                      cx="50" cy="50" r="42" 
                      className={`stroke-current transition-all duration-1000 ease-out ${progress >= 50 && progress < 100 ? 'text-emerald-400' : progress === 100 ? 'text-emerald-500' : 'text-blue-500'}`} 
                      strokeWidth="6" fill="transparent" 
                      strokeDasharray={`${progress * 2.64} 264`}
                      strokeLinecap="round" 
                   />
                 </svg>
                 <div className="absolute flex flex-col items-center justify-center transform group-hover:scale-105 transition-transform">
                   <span className="text-5xl font-black text-slate-800 tracking-tighter">{progress}%</span>
                   <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">{progress === 100 ? 'Unlocked' : 'Activated'}</span>
                 </div>
              </div>
              
              {/* Emotional Progress Messaging */}
              <div className={`mt-4 w-full p-3 rounded-xl border ${progress >= 50 ? 'bg-emerald-50/50 border-emerald-100' : 'bg-blue-50 border-blue-100'}`}>
                 {renderProgressMessage()}
              </div>

              {/* Dynamic CTA Engine */}
              {progress === 100 ? (
                 <div className="mt-4 w-full animate-fade-in-up">
                    <button 
                      onClick={() => window.location.href = PlatformConfig.getModuleUrl('erp')}
                      className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm rounded-xl shadow-lg shadow-emerald-200 hover:shadow-xl hover:-translate-y-0.5 transition-all border border-transparent"
                    >
                      🚀 Enter Digital Campus 
                    </button>
                 </div>
              ) : (
                 <div className="mt-4 w-full animate-fade-in-up">
                   <button 
                      onClick={handleContinueSetup}
                      className="w-full py-3.5 bg-gray-900 text-white font-bold text-sm rounded-xl shadow-md hover:shadow-lg hover:bg-black hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 group"
                    >
                      <span>Continue Setup</span>
                      <ArrowRightIcon className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors group-hover:translate-x-1 duration-300" />
                    </button>
                 </div>
              )}
            </div>
        </div>

        {/* RIGHT: Go-Live Countdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col transition-all hover:shadow-md">
           <div className="flex items-center gap-3 mb-6 border-b border-gray-50 pb-3">
             <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm">3</div>
             <h3 className="text-lg font-bold text-gray-800">Launch Timeline</h3>
           </div>
           
           <div className="flex-1 flex flex-col items-center justify-center transform scale-95 origin-center">
              <GoLiveCountdown tenantId={id} />
           </div>
           
           <div className="mt-6 text-center px-2">
             <p className="text-[10px] text-gray-400 font-medium leading-relaxed">
               This intelligent countdown tracks your deployment date. Knock out tasks to stay ahead of launch day.
             </p>
           </div>
        </div>

      </div>
    </div>
  );
};

export default ModuleLauncher;
